import { service } from '@ember/service';
import Component from '@glimmer/component';

import type { Service } from 'webapp-types';
import { BillingFeature } from 'webapp/enums/enums';

import CommonUiIconComponent from 'webapp/components/common/ui-icon';

import type BillingPlan from 'webapp/models-custom/billing-plan';

interface Args {
	model: BillingPlan;
}

interface TopFeatureInfo {
	name: string;
	value: string | number | boolean;
	icon?: IconName;
}

export default class BillingPlanTopFeaturesComponent extends Component<Args> {
	@service('billing') declare billingService: Service.Billing;

	get topFeatures(): TopFeatureInfo[] {
		const isCustomPlan = this.args.model.isCustom;

		let usersFeature: TopFeatureInfo;
		{
			const usersValue = this.args.model.getFeatureValue(BillingFeature.USERS);

			const usersText =
				usersValue === null
					? isCustomPlan
						? 'More users'
						: 'Unlimited users'
					: `user${usersValue === 1 ? '' : 's'}`;

			usersFeature = {
				name: usersText,
				value: usersValue === null ? (isCustomPlan ? '' : '∞') : usersValue,
			};
		}

		let aiFeature: TopFeatureInfo;
		{
			const aiCreditsValue = this.args.model.isCustom
				? null
				: this.args.model.getFeatureValue(BillingFeature.AI_CREDITS);

			let aiCreditsText = 'AI credits/mo';
			if (!aiCreditsValue) {
				aiCreditsText = `More ${aiCreditsText}`;
			}
			aiFeature = {
				name: aiCreditsText,
				value: aiCreditsValue || '',
				icon: 'AiFill',
			};
		}

		if (this.billingService.is2026SeptBillingGroup) {
			const sourcesValue = this.args.model.getFeatureValue(BillingFeature.SOURCES);
			const sourcesText =
				sourcesValue === null
					? isCustomPlan
						? 'More data sources'
						: 'Unlimited data sources'
					: `data source${sourcesValue === 1 ? '' : 's'}`;
			const sourcesFeature = {
				name: sourcesText,
				value: sourcesValue === null ? (isCustomPlan ? '' : '∞') : sourcesValue,
			};
			return [usersFeature, sourcesFeature, aiFeature];
		} else {
			return [usersFeature, aiFeature];
		}
	}

	<template>
		<div class="plan__features plan__features--simple">
			<ul>
				{{#each this.topFeatures as |feature|}}
					<li>
						{{#if feature.value}}
							<span class="value">{{feature.value}}</span>
						{{/if}}
						{{feature.name}}
						{{#if feature.icon}}
							<CommonUiIconComponent
								@name={{feature.icon}}
								@size="sm"
								@gradient="orange-violet"
							/>
						{{/if}}
					</li>
				{{/each}}
			</ul>
		</div>
	</template>
}
